// Query options for the S1-16 screens: one place for keys and SDK calls, so a screen's call
// budget (at most 3 on load, acceptance 3) is visible from the queries it mounts. `kpi`
// queries are only ever mounted behind `useFeature("kpi")`, so a disabled add-on never
// reaches a `kpi` route (ADR-0016; acceptance 5).
import { ApiError } from "@companyos/sdk";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Api } from "../api/client";
import type { RecordSignal, WorkforceAgentRow } from "../api/types";

export const keys = {
  operatorCompanies: (week: string) => ["operator", "companies", week] as const,
  operatorConsumers: () => ["operator", "consumers"] as const,
  companyScorecard: (co: string, week: string) => ["company", co, "scorecard", week] as const,
  digitalEmployees: (co: string) => ["company", co, "digital-employees"] as const,
  /** WORKFORCE: the company's agents (AG). */
  workforceAgents: (co: string) => ["company", co, "workforce-agents"] as const,
  seatScorecard: (co: string, seat: string, week: string) =>
    ["company", co, "seat", seat, "scorecard", week] as const,
  reports: (co: string) => ["company", co, "reports"] as const,
  seatMetricSet: (co: string, seat: string) => ["company", co, "seat", seat, "metric-set"] as const,
  myNumbers: (co: string) => ["company", co, "me", "numbers"] as const,
  de: (co: string, id: string) => ["company", co, "de", id] as const,
  deScorecard: (co: string, id: string, week: string) =>
    ["company", co, "de", id, "scorecard", week] as const,
  deTimeline: (co: string, id: string) => ["company", co, "de", id, "timeline"] as const,
  deRecord: (co: string, id: string, weeks: number) =>
    ["company", co, "de", id, "record", weeks] as const,
  deRecordEvidence: (co: string, id: string, week: string, signal: string | null) =>
    ["company", co, "de", id, "record", "evidence", week, signal] as const,
  memoryUse: (co: string, days: number) => ["company", co, "kpis", "memory-use", days] as const,
  memoryGate: (co: string) => ["company", co, "kpis", "memory-gate"] as const,
  seatCost: (co: string, seat: string, month: string) =>
    ["company", co, "seat", seat, "cost", month] as const,
  seatBoardWakes: (co: string, seat: string) =>
    ["company", co, "seat", seat, "board-wakes"] as const,
  timeEntries: (co: string, seat: string, week: string) =>
    ["company", co, "seat", seat, "time", week] as const,
  // S1-17
  gate: (asOf: string | null) => ["operator", "gate", asOf ?? "today"] as const,
  company: (co: string) => ["company", co, "detail"] as const,
  people: (co: string) => ["company", co, "people"] as const,
  invitations: (co: string) => ["company", co, "invitations"] as const,
  seats: (co: string) => ["company", co, "seats"] as const,
  subscriptions: (co: string) => ["company", co, "de-subscriptions"] as const,
  metrics: (co: string) => ["company", co, "metrics"] as const,
  targets: (co: string) => ["company", co, "targets"] as const,
  companyObservations: (co: string) => ["company", co, "observations", "company"] as const,
  weekTime: (co: string, week: string) => ["company", co, "time", week] as const,
  imports: (co: string) => ["company", co, "imports"] as const,
  weekReports: (co: string, week: string) => ["company", co, "reports", "week", week] as const,
  /** Keyed by the stored artefact too (review #2): a PATCH that re-renders gets a new key. */
  reportHtml: (co: string, id: string, artefact?: string) =>
    ["company", co, "reports", "html", id, ...(artefact === undefined ? [] : [artefact])] as const,
};

export const gateQuery = (api: Api, asOf: string | null) =>
  queryOptions({
    queryKey: keys.gate(asOf),
    queryFn: () => api.client.operator.gate.get({ query: asOf ? { as_of: asOf } : {} }),
  });

export const companyQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: keys.company(co),
    queryFn: () => api.client.companies.get({ params: { co } }),
  });

/**
 * Setup lists and pickers: cursor-paged (S1-17 review #8), `PAGE.setup` rows a page; every
 * screen follows `next_cursor` ("Load more", or a picker's search), nothing is capped.
 */
const paged = <T extends { next_cursor: string | null }>(
  queryKey: readonly unknown[],
  fetch: (query: { limit: number; cursor?: string }) => Promise<T>,
) =>
  infiniteQueryOptions({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fetch({ limit: PAGE.setup, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: next,
  });

export const peopleQuery = (api: Api, co: string) =>
  paged(keys.people(co), (query) => api.client.people.list({ params: { co }, query }));

export const invitationsQuery = (api: Api, co: string) =>
  paged(keys.invitations(co), (query) => api.client.invitations.list({ params: { co }, query }));

export const seatsQuery = (api: Api, co: string) =>
  paged(keys.seats(co), (query) => api.client.seats.list({ params: { co }, query }));

export const subscriptionsQuery = (api: Api, co: string) =>
  paged(keys.subscriptions(co), (query) =>
    api.client.deSubscriptions.list({ params: { co }, query }),
  );

export const metricsQuery = (api: Api, co: string) =>
  paged(keys.metrics(co), (query) => api.client.metrics.list({ params: { co }, query }));

export const targetsQuery = (api: Api, co: string) =>
  paged(keys.targets(co), (query) => api.client.targets.list({ params: { co }, query }));

/** The company's own observations (subject = company), newest period first. */
/** A seat's own entered values (Enter numbers with a seat picked, KPI-FIX #6), newest first. */
export const seatObservationsQuery = (api: Api, co: string, seat: string) =>
  infiniteQueryOptions({
    queryKey: ["company", co, "observations", "seat", seat] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.observations.list({
        params: { co },
        query: {
          subject_kind: "seat",
          subject_id: seat,
          limit: PAGE.observations,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
  });

export const companyObservationsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: keys.companyObservations(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.observations.list({
        params: { co },
        query: {
          subject_kind: "company",
          subject_id: co,
          limit: PAGE.observations,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
  });

/** Every seat's time entries of a week (the hours form). */
export const weekTimeQuery = (api: Api, co: string, week: string) =>
  infiniteQueryOptions({
    queryKey: keys.weekTime(co, week),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.timeEntries.list({
        params: { co },
        query: { period: week, limit: PAGE.time, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: next,
  });

export const importsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: keys.imports(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.imports.list({
        params: { co },
        query: { limit: PAGE.imports, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: next,
  });

/** Every version of one week's report (newest first). */
export const weekReportsQuery = (api: Api, co: string, week: string) =>
  queryOptions({
    queryKey: keys.weekReports(co, week),
    queryFn: () => api.client.reports.list({ params: { co }, query: { week, limit: 50 } }),
  });

/**
 * The stored HTML of one report version. The key carries the artefact (object key and the
 * row's `updated_at`), so the cache can never serve HTML from before an edit (review #2).
 */
export const reportHtmlQuery = (
  api: Api,
  co: string,
  record: { id: string; html_object_key: string | null; updated_at: string },
) =>
  queryOptions({
    queryKey: keys.reportHtml(
      co,
      record.id,
      `${record.html_object_key ?? ""}@${record.updated_at}`,
    ),
    queryFn: () => api.client.reports.html({ params: { co, id: record.id } }),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const operatorCompaniesQuery = (api: Api, week: string) =>
  queryOptions({
    queryKey: keys.operatorCompanies(week),
    queryFn: () => api.client.operator.companies.list({ query: { week } }),
  });

export const operatorConsumersQuery = (api: Api) =>
  queryOptions({
    queryKey: keys.operatorConsumers(),
    queryFn: () => api.client.operator.consumers.list(),
  });

export const companyScorecardQuery = (api: Api, co: string, week: string) =>
  queryOptions({
    queryKey: keys.companyScorecard(co, week),
    queryFn: () => api.client.scorecards.company({ params: { co }, query: { period: week } }),
  });

/**
 * Page sizes. Lists are cursor-paginated (04 §Pagination): the first page is the screen's one
 * call, further pages load on "Load more" (never on screen load, so the budget holds).
 */
export const PAGE = {
  des: 100,
  reports: 8,
  time: 100,
  timeline: 50,
  observations: 50,
  imports: 20,
  setup: 50,
} as const;

const next = (last: { next_cursor: string | null }) => last.next_cursor ?? undefined;

/**
 * `includeRemoved` (PURGE-RETIRED-AGENTS): removed DEs too (the roster's "Show retired", so a
 * removed one can still be removed permanently); its key sits under the same prefix.
 */
export const digitalEmployeesQuery = (api: Api, co: string, includeRemoved = false) =>
  infiniteQueryOptions({
    queryKey: includeRemoved
      ? ([...keys.digitalEmployees(co), "with-removed"] as const)
      : keys.digitalEmployees(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.digitalEmployees.list({
        params: { co },
        query: {
          limit: PAGE.des,
          ...(includeRemoved ? { include_removed: "true" as const } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
  });

/**
 * WORKFORCE: the company's agents (AG), every page at once (a company has tens, not thousands;
 * the sidebar, the Agents list and the seat tree share this one cached read). `includeRetired`:
 * retired ones too (the Agents list's "Show retired", so a retired agent can be reactivated);
 * its key sits under the same prefix, so every invalidation reaches both.
 */
export const workforceAgentsQuery = (api: Api, co: string, includeRetired = false) =>
  queryOptions({
    queryKey: includeRetired
      ? ([...keys.workforceAgents(co), "with-retired"] as const)
      : keys.workforceAgents(co),
    queryFn: async () => {
      const out: WorkforceAgentRow[] = [];
      let cursor: string | undefined;
      do {
        const p = await api.client.workforceAgents.list({
          params: { co },
          query: {
            limit: 200,
            ...(includeRetired ? { include_retired: "true" as const } : {}),
            ...(cursor ? { cursor } : {}),
          },
        });
        out.push(...p.data);
        cursor = p.next_cursor ?? undefined;
      } while (cursor);
      return out;
    },
  });

/** WF-FIX: one agent (AG) for its own page; under the list's prefix, so a change refreshes it. */
export const workforceAgentQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...keys.workforceAgents(co), "one", id] as const,
    queryFn: () => api.client.workforceAgents.get({ params: { co, id } }),
  });

/** MEMBER-ENTRY: the caller's own seats and the numbers due on each (`GET me/numbers`). */
export const myNumbersQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: keys.myNumbers(co),
    queryFn: () => api.client.me.numbers({ params: { co } }),
  });

/** A seat's scorecard for a period of any type (My scorecard's weeks, months or quarters). */
export const seatPeriodScorecardQuery = (
  api: Api,
  co: string,
  seat: string,
  type: "week" | "month" | "quarter",
  period: string,
) =>
  queryOptions({
    queryKey: [...keys.seatScorecard(co, seat, period), type] as const,
    queryFn: () =>
      api.client.seats.scorecard({
        params: { co, id: seat },
        query: { period_type: type, period },
      }),
  });

export const seatScorecardQuery = (api: Api, co: string, seat: string, week: string) =>
  queryOptions({
    queryKey: keys.seatScorecard(co, seat, week),
    queryFn: () =>
      api.client.seats.scorecard({
        params: { co, id: seat },
        query: { period_type: "week", period: week },
      }),
  });

/** The seat's whole metric set and its version (`seats.metrics`): what the set editor saves over. */
export const seatMetricSetQuery = (api: Api, co: string, seat: string) =>
  queryOptions({
    queryKey: keys.seatMetricSet(co, seat),
    queryFn: () => api.client.seats.metrics({ params: { co, id: seat } }),
  });

export const reportsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: keys.reports(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.reports.list({
        params: { co },
        query: { limit: PAGE.reports, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: next,
  });

export const deQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: keys.de(co, id),
    queryFn: () => api.client.digitalEmployees.get({ params: { co, id } }),
  });

export const deScorecardQuery = (api: Api, co: string, id: string, week: string) =>
  queryOptions({
    queryKey: keys.deScorecard(co, id, week),
    queryFn: () =>
      api.client.digitalEmployees.scorecard({
        params: { co, id },
        query: { period_type: "week", period: week },
      }),
  });

export const seatCostQuery = (api: Api, co: string, seat: string, month: string) =>
  queryOptions({
    queryKey: keys.seatCost(co, seat, month),
    queryFn: () =>
      api.client.seats.cost({
        params: { co, id: seat },
        query: { period_type: "month", period: month },
      }),
  });

/** T2-BOARD-WAKE: the board wakes of the DE on a seat over the last 7 days (its Cost tab). */
export const seatBoardWakesQuery = (api: Api, co: string, seat: string) =>
  queryOptions({
    queryKey: keys.seatBoardWakes(co, seat),
    queryFn: () => api.client.seats.board_wakes({ params: { co, id: seat }, query: { days: 7 } }),
  });

export const timeEntriesQuery = (api: Api, co: string, seat: string, week: string) =>
  infiniteQueryOptions({
    queryKey: keys.timeEntries(co, seat, week),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.timeEntries.list({
        params: { co },
        query: {
          seat,
          period: week,
          limit: PAGE.time,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
  });

export const deTimelineQuery = (api: Api, co: string, id: string) =>
  infiniteQueryOptions({
    queryKey: keys.deTimeline(co, id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.digitalEmployees.timeline({
        params: { co, id },
        query: { limit: PAGE.timeline, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: next,
  });

/** AGENT-RECORD: the DE's record over the last `weeks` weeks (one call). */
export const deRecordQuery = (api: Api, co: string, id: string, weeks: number) =>
  queryOptions({
    queryKey: keys.deRecord(co, id, weeks),
    queryFn: () => api.client.digitalEmployees.record({ params: { co, id }, query: { weeks } }),
  });

/** RECALL-USAGE: the memory use share by day, company-wide and per DE (counts only). */
export const memoryUseQuery = (api: Api, co: string, days: number) =>
  queryOptions({
    queryKey: keys.memoryUse(co, days),
    queryFn: () => api.client.kpis.memoryUse({ params: { co }, query: { days } }),
  });

/** MEMORY-FIT: memory's eight release-gate metrics, the last two weeks (counts only). */
export const memoryGateQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: keys.memoryGate(co),
    queryFn: () => api.client.kpis.memoryGate({ params: { co }, query: {} }),
  });

/** What one week of the record counts (one signal, or all), newest first, cursor-paged. */
export const deRecordEvidenceQuery = (
  api: Api,
  co: string,
  id: string,
  week: string,
  signal: RecordSignal | null,
) =>
  infiniteQueryOptions({
    queryKey: keys.deRecordEvidence(co, id, week, signal),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.digitalEmployees.recordEvidence({
        params: { co, id },
        query: {
          week,
          limit: PAGE.timeline,
          ...(signal ? { signal } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
  });

// Structure (seats, roles, metric templates, scorecard publications) ---------------------------

export const structureKeys = {
  roles: (co: string) => ["company", co, "roles"] as const,
  seat: (co: string, id: string) => ["company", co, "seats", "detail", id] as const,
  metricTemplates: (co: string) => ["company", co, "metrics", "templates"] as const,
  publications: (co: string) => ["company", co, "publications"] as const,
};

export const rolesQuery = (api: Api, co: string) =>
  paged(structureKeys.roles(co), (query) => api.client.roles.list({ params: { co }, query }));

/** One seat with its active assignments (the seat drawer). */
export const seatQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: structureKeys.seat(co, id),
    queryFn: () => api.client.seats.get({ params: { co, id } }),
  });

/** The seeded metric catalog per DE template (read-only, never changes at runtime). */
export const metricTemplatesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: structureKeys.metricTemplates(co),
    queryFn: () => api.client.metrics.templates({ params: { co } }),
    staleTime: Number.POSITIVE_INFINITY,
  });

/** Every publication revision of the company, newest first. */
export const publicationsQuery = (api: Api, co: string) =>
  paged(structureKeys.publications(co), (query) =>
    api.client.scorecards.publications({ params: { co }, query }),
  );

// Responsibilities, recurring work and templates (M1-05, M1-06) --------------------------------

export const orgKeys = {
  responsibilities: (co: string, seat: string) =>
    ["company", co, "responsibilities", seat] as const,
  /** Every recurring-work list of the company (archiving a responsibility pauses its work). */
  allRecurringWork: (co: string) => ["company", co, "recurring-work"] as const,
  recurringWork: (co: string, responsibility: string) =>
    ["company", co, "recurring-work", responsibility] as const,
  templates: (co: string) => ["company", co, "templates"] as const,
  template: (co: string, id: string) => ["company", co, "templates", "detail", id] as const,
  teams: (co: string) => ["company", co, "teams"] as const,
  orgChart: (co: string) => ["company", co, "org-chart"] as const,
};

/** A seat's responsibilities, archived ones included (the seat drawer). */
export const responsibilitiesQuery = (api: Api, co: string, seat: string) =>
  paged(orgKeys.responsibilities(co, seat), (query) =>
    api.client.responsibilities.list({ params: { co, id: seat }, query }),
  );

/** A responsibility's recurring work. */
export const recurringWorkQuery = (api: Api, co: string, responsibility: string) =>
  paged(orgKeys.recurringWork(co, responsibility), (query) =>
    api.client.recurringWork.list({ params: { co, id: responsibility }, query }),
  );

export const templatesQuery = (api: Api, co: string) =>
  paged(orgKeys.templates(co), (query) => api.client.templates.list({ params: { co }, query }));

/** One template (a `?template=` link to one beyond the first page of the list). */
export const templateQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: orgKeys.template(co, id),
    queryFn: () => api.client.templates.get({ params: { co, id } }),
  });

// Personal crews (M4-01): the signed-in person's own agent, `agents` add-on only ---------------

export const crewKeys = {
  me: (co: string) => ["company", co, "crews", "me"] as const,
  // M4-03: the crew features.
  reminders: (co: string) => ["company", co, "crews", "reminders"] as const,
  memoryGrants: (co: string) => ["company", co, "crews", "memory-grants"] as const,
  proposals: (co: string) => ["company", co, "crews", "proposals"] as const,
};

/** My reminders (M4-03): open ones on the seats I hold, soonest first, and the day's fired. */
export const crewRemindersQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: crewKeys.reminders(co),
    queryFn: () => api.client.crews.reminders({ params: { co } }),
  });

/** The role memory my agent may read, and what I may grant (M4-03, OQ 37). */
export const crewMemoryGrantsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: crewKeys.memoryGrants(co),
    queryFn: () => api.client.crews.memoryGrants({ params: { co } }),
  });

/** My agent's pending proposals (M4-03), newest first: the Inbox drawer's suggestions. */
export const crewProposalsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: crewKeys.proposals(co),
    queryFn: () => api.client.crews.proposals({ params: { co } }),
  });

/**
 * My crew, or null while it is being set up (`GET crews/me` is 404 until the `crews.setup`
 * consumer made it): a state of the screen, not a failure to toast.
 */
export const myCrewQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: crewKeys.me(co),
    queryFn: async () => {
      try {
        return await api.client.crews.me({ params: { co } });
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
